import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { AlertsService } from '../../services/alerts.service';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { ViewportService } from '../../services/viewport.service';
import { SearchService } from '../../search/services/search.service';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { SaveSearchComponent } from '../save-search/save-search.component';
import { formatString } from '../../utils/string-utils';
import dayjs from 'dayjs';
import flatpickr from 'flatpickr';

/**
 * Main saved search management component.
 */
@Component({
	selector: 'saved-searches',
	templateUrl: './saved-searches.component.html',
	styleUrl: './saved-searches.component.css'
})
export class SavedSearchesComponent implements OnInit, OnDestroy {
	
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isMobile: boolean = false;

	datePicker: any;

	activeTab = 1;
	inprogress: boolean = false;
	showCreator: boolean = false;

	mode: string = 'mine';
	searches: any[] = [];
	selectedSearches = {
		selected: [] as number[],
		canSubscribe: false,
		canUnsubscribe: false,
		canFavorite: false,
		canUnfavorite: false
	};

	filters = {
		username_input: '',
		usernames: [] as string[],
		searchName: '',
		searchStatus: '',
		dateRange: '',
		startDate: '',
		endDate: ''
	};
	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		elementsPerPageSizes: [10, 20, 50],
		sorting: {
			sort: 'created,desc'
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			if (this.mode === 'mine') {
				this.loadSavedSearches(this.pagination.page);
			} else {
				this.loadPublicSearches(this.pagination.page);
			}
		}
	};
	private isSortDirAsc: boolean = true;

	constructor(
		private viewportService: ViewportService,
		private ngbModal: NgbModal,
		private configService: ConfigService,
		private userService: UserService,
		private alertsService: AlertsService,
		private searchService: SearchService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.showCreator = this.configService.getConfig()?.advancedSearchPanel?.savedSearches?.showCreator;

		// Replacement for the viewportsizemonitor directive
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});	
		
		this.loadSavedSearches(1);
	}
	
	ngOnDestroy(): void {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}	

	onTabChange(event: NgbNavChangeEvent) {
		this.searches = [];
		this.clear(false);

		if (event.nextId === 1) {
			this.mode = 'mine';
			this.loadSavedSearches(1);
		} else if (event.nextId === 2) {
			this.mode = 'public';
			this.loadPublicSearches(1);
		}
	}

	private reload(): void {
		if (this.mode === 'mine') {
			this.loadSavedSearches();
		} else {
			this.loadPublicSearches();
		}
	}

	/**
	 * Loads the user's saved searches using the current constraints.
	 */
	loadSavedSearches(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.searchService.getMySavedSearches(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.searches = [];
				} else {
					this.searches = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
				this.updateSearchStatus();
			},
			(err: any) => {
				console.error("Failed to fetch user's saved searches", err);
				this.searches = [];
				this.inprogress = false;
				this.updateSearchStatus();
			}
		);
	}

	/**
	 * Loads the publicly-available saved searches using the current constraints.
	 */
	loadPublicSearches(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.searchService.getMySavedSearchesFiltered(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.searches = [];
				} else {
					this.searches = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
				this.updateSearchStatus();
			},
			(err: any) => {
				console.error("Failed to fetch user's saved searches", err);
				this.searches = [];
				this.inprogress = false;
				this.updateSearchStatus();
			}
		);
	}

	removeUsername(index: number): void {
		this.filters.usernames.splice(index, 1);
		this.filter();
	}

	submitUsername(): void {
		if (this.filters.username_input.length > 0) {
			this.filters.usernames = [...this.filters.usernames, this.filters.username_input];
			this.filters.username_input = '';
		}
	}

	clear(doReload: boolean = true): void {
		this.constraints = {};
		
		if (this.datePicker) {
			this.datePicker.clear();
		}

		this.filters.username_input = '';
		this.filters.usernames = [];
		this.filters.searchName = '';
		this.filters.searchStatus = '';
		this.filters.dateRange = '';
		this.filters.startDate = '';
		this.filters.endDate = '';
		
		if (doReload) {
			this.reload();
		}
	}

	filter(): void {
		if (this.datePicker) {
			let [start, end] = this.datePicker.selectedDates;

			if (start) {
				if (!end) end = new Date(start.getTime());
				start.setHours(0, 0, 0, 0);
				end.setHours(23, 59, 59, 999);
				console.log("Selected range:", {
					start: dayjs(start).format("MM/DD/YYYY h:m:s"),
					end: dayjs(end).format("MM/DD/YYYY h:m:s"),
				});
				this.filters.startDate = start.getTime();
				this.filters.endDate = end.getTime();
			}
		}

		if (this.filters.username_input.length > 0) {
			this.filters.usernames = this.filters.usernames || [];
			this.filters.usernames = [...this.filters.usernames, this.filters.username_input];
			this.filters.username_input = '';
		}

		this.constraints = {};

		if (this.filters.usernames.length > 0) {
			this.constraints['usernames'] = this.filters.usernames;
		}
		if (this.filters.startDate) {
			this.constraints['beginCreatedDate'] = this.filters.startDate;
		}
		if (this.filters.endDate) {
			this.constraints['endCreatedDate'] = this.filters.endDate;
		}
		if (this.filters.searchName) {
			this.constraints['name'] = this.filters.searchName;
		}
		if (this.filters.searchStatus) {
			this.constraints['status'] = this.filters.searchStatus;
		}

		this.reload();
	}

	pageSizeChanged(size: number): void {
		this.pagination.elementsPerPage = size;
		this.reload();
	}

	sortingChanged(sortby: string): void {
		this.isSortDirAsc = !this.isSortDirAsc;
		let direction = this.isSortDirAsc ? 'asc' : 'desc';
		this.pagination.sorting = { sort: sortby + ',' + direction };
		this.reload();
	}

	toggleSearchId(id: number): void {
		const index = this.selectedSearches.selected.indexOf(id);
		if (index > -1) {
			this.selectedSearches.selected.splice(index, 1);
		} else {
			this.selectedSearches.selected.push(id);
		}

		this.updateSearchStatus();
	}

	private updateSearchStatus(): void {
		this.selectedSearches.canSubscribe = false;
		this.selectedSearches.canUnsubscribe = false;
		this.selectedSearches.canFavorite = false;
		this.selectedSearches.canUnfavorite = false;

		let allCanSubscribe = true;
		let allCanUnsubscribe = true;
		let allCanFavorite = true;
		let allCanUnfavorite = true;

		this.searches.forEach((search: any) => {
			if (this.selectedSearches.selected.includes(search.id)) {
				if (search.subscriptionId) allCanSubscribe = false;
				if (!search.subscriptionId) allCanUnsubscribe = false;
				if (search.isFavorite) allCanFavorite = false;
				if (!search.isFavorite) allCanUnfavorite = false;
			}
		});

		if (this.selectedSearches.selected.length > 0) {
			this.selectedSearches.canSubscribe = allCanSubscribe;
			this.selectedSearches.canUnsubscribe = allCanUnsubscribe;
			this.selectedSearches.canFavorite = allCanFavorite;
			this.selectedSearches.canUnfavorite = allCanUnfavorite;
		}
	}

	openDatePicker(id: string): void {
		if (!this.datePicker) {
			const opts = this.userService.getDatePickerOptions();
			opts.mode = "range";
			delete opts.defaultDate;

			const element = document.getElementById(id);
			if (element) {
				this.datePicker = flatpickr(element, opts);
			}
		}

		if (this.datePicker && this.datePicker.isOpen) {
			this.datePicker.close();
		} else if (this.datePicker) {
			this.datePicker.open();
		}
	}

	/**
	 * Deletes the saved search with the given id.
	 */
	onDeleteSearch(id: number): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteSearch',
			title: this.LOCALIZATION.deleteSavedSearchHeader,
			message: this.LOCALIZATION.deleteSavedSearchMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.searchService.deleteSearch(id).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.deleteSavedSearchSuccess
							});
							this.selectedSearches.selected = [];
							this.loadSavedSearches(1);
						},
						(error: any) => {
							console.error("Failed to delete saved search", error);
							this.alertsService.error({
								message: this.LOCALIZATION.deleteSavedSearchError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	executeSearch(searchObj: any): void {
		let payload = {
			searchObj: searchObj,
			notify: false
		};
		this.searchService.broadcastSearchEvent(payload);
	}

	updatePublic(search: any): void {
		let payload = {
			isPublic: search.isPublic,
			name: search.name,
			description: search.description
		};

		this.searchService.updateSearch(search.id, payload).then(
			(response: any) => {
				this.inprogress = false;
			},
			(error: any) => {
				console.error("Failed to update saved search", error);
				this.inprogress = false
			}
		);
	}

	viewSearch(search: any, canEdit: boolean): void { 
		const modalRef = this.ngbModal.open(SaveSearchComponent, {
			size: 'md'
		});
		
		modalRef.componentInstance.mode = (canEdit ? "update" : "view");
		modalRef.componentInstance.searchData = search.data;
		modalRef.componentInstance.savedSearch = search;

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.reload();
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}

	openRSS(search: any): void {
		let url = formatString(this.configService.getConfig().notifications.georssURL, search.id);
		window.open(url);
	}

	/**
	 * Unsubscribes the user from the saved search with the given id.
	 */
	unsubscribeFromSearch(id: number): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'unsubscribeFromSearch',
			title: this.LOCALIZATION.unsubscribeSavedSearchHeader,
			message: this.LOCALIZATION.unsubscribeSavedSearchMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.searchService.unsubscribeFromSearch(id).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.unsubscribeSavedSearchSuccess
							});
							this.selectedSearches.selected = [];
							this.reload();
						},
						(error: any) => {
							console.error("Failed to unsubscribe from the saved search", error);
							this.alertsService.error({
								message: this.LOCALIZATION.unsubscribeSavedSearchError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Unsubscribes the user from the chosen saved searches.
	 */
	massUnsubscribeFromSearches(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'massUnsubscribeFromSearches',
			title: this.LOCALIZATION.unsubscribeSavedSearchesHeader,
			message: this.LOCALIZATION.unsubscribeSavedSearchesMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.searchService.massUnsubscribeFromSearches(this.selectedSearches.selected).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.unsubscribeSavedSearchesSuccess
							});
							this.selectedSearches.selected = [];
							this.reload();
						},
						(error: any) => {
							console.error("Failed to unsubscribe from the saved searches", error);
							this.alertsService.error({
								message: this.LOCALIZATION.unsubscribeSavedSearchesError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Subscribes the user to the saved search with the given id.
	 */
	subscribeToSearch(id: number): void {
		this.searchService.subscribeToSearch(id).then(
			(response: any) => {
				this.selectedSearches.selected = [];
				this.reload();
			},
			(error: any) => {
				if (error.status && error.status >= 400 && error.status < 499) {
					this.alertsService.info({
						message: "Already subscribed to the search"
					});
				} else {
					console.error("Failed to subscribe to the saved search", error);
					this.alertsService.error({
						message: "Failed to subscribe to this search."
					});
				}
			}
		);
	}

	/**
	 * Subscribes the user to the chosen saved searches.
	 */
	massSubscribeToSearches(): void {
		this.searchService.massSubscribeToSearches(this.selectedSearches.selected).then(
			(response: any) => {
				this.selectedSearches.selected = [];
				this.reload();
			},
			(error: any) => {
				console.error("Failed to subscribe to the saved searches", error);
				this.alertsService.error({
					message: "Failed to subscribe to these searches."
				});
			}
		);
	}

	/**
	 * Adds the saved search with the given id to the user's favorites.
	 */
	favoriteASearch(id: number): void {
		this.searchService.favoriteSearch(id).then(
			(response: any) => {
				this.reload();
			},
			(error: any) => {
				console.error("Failed to add the saved search to favorites", error);
				this.alertsService.error({
					message: "Failed to add search to favorites."
				});
			}
		);
	}

	/**
	 * Adds the chosen saved searches to the user's favorites.
	 */
	massFavoriteSearches(): void {
		this.searchService.massFavoriteSearch(this.selectedSearches.selected).then(
			(response: any) => {
				this.selectedSearches.selected = [];
				this.reload();
			},
			(error: any) => {
				console.error("Failed to add the saved searches to favoritess", error);
				this.alertsService.error({
					message: "Failed to add to favorites."
				});
			}
		);
	}

	/**
	 * Removes the saved search with the given id from the user's favorites.
	 */
	unfavoriteASearch(id: number): void {
		this.searchService.unfavoriteSearch(id).then(
			(response: any) => {
				this.reload();
			},
			(error: any) => {
				console.error("Failed to remove the saved search from favorites", error);
				this.alertsService.error({
					message: "Failed to remove search from favorites."
				});
			}
		);
	}

	/**
	 * Removes the chosen saved searches from the user's favorites.
	 */
	massUnfavoriteSearches(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'massUnfavoriteSearches',
			title: this.LOCALIZATION.unfavoriteHeader,
			message: this.LOCALIZATION.unfavoriteMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.searchService.massUnfavoriteSearch(this.selectedSearches.selected).then(
						(response: any) => {
							this.selectedSearches.selected = [];
							this.reload();
						},
						(error: any) => {
							console.error("Failed to remove the saved searches from favorites", error);
							this.alertsService.error({
								message: this.LOCALIZATION.unfavoriteError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}
}
